import type { WizardModeProps } from "../../types/wizard.types";
import type {
  MeteringOrMarketLocation,
  Person
} from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import type { ConnectionResponse } from "../ConnectionWizard/Connection.types";
import { ConnectionWizard } from "../ConnectionWizard/ConnectionWizard";
import { MarketLocationWizard } from "../MarketLocationWizard/MarketLocationWizard";
import { MeteringLocationWizard } from "../MeteringLocationWizard/MeteringLocationWizard";
import { PersonWizard } from "../PersonWizard/PersonWizard";

type WizardPayloadMap = {
  person: Person;
  connection: ConnectionResponse;
  meteringLocation: MeteringOrMarketLocation;
  marketLocation: MeteringOrMarketLocation;
};

type WizardKey = keyof WizardPayloadMap;

type WizardComponentProps<T> = {
  isOpen: boolean;
  modeProps: WizardModeProps;
  siteId: number;
  stackId: string;
  variantId: number;
  onClose: () => void;
  onNestedSave: (object: T) => void;
};

type FieldWizardEntry<T> = {
  displayName: string;
  Component: (props: WizardComponentProps<T>) => React.JSX.Element;
};

const FIELD_WIZARD_REGISTRY = {
  person: {
    displayName: "Unternehmen",
    Component: ({
      isOpen,
      modeProps,
      siteId,
      stackId,
      variantId,
      onClose,
      onNestedSave
    }: WizardComponentProps<Person>) => (
      <PersonWizard
        context="nested"
        isOpen={isOpen}
        siteId={siteId}
        size="lg"
        stackId={stackId}
        variantId={variantId}
        onClose={onClose}
        onNestedSave={onNestedSave}
        {...modeProps}
      />
    )
  },
  connection: {
    displayName: ObjectName.Connection,
    Component: ({
      isOpen,
      modeProps,
      siteId,
      stackId,
      variantId,
      onClose,
      onNestedSave
    }: WizardComponentProps<ConnectionResponse>) => (
      <ConnectionWizard
        context="nested"
        isOpen={isOpen}
        siteId={siteId}
        size="lg"
        stackId={stackId}
        variantId={variantId}
        onClose={onClose}
        onNestedSave={onNestedSave}
        {...modeProps}
      />
    )
  },
  meteringLocation: {
    displayName: ObjectName.MeteringLocation,
    Component: ({
      isOpen,
      modeProps,
      siteId,
      stackId,
      onClose,
      onNestedSave
    }: WizardComponentProps<MeteringOrMarketLocation>) => (
      <MeteringLocationWizard
        context="nested"
        isOpen={isOpen}
        siteId={siteId}
        size="lg"
        stackId={stackId}
        onClose={onClose}
        onNestedSave={onNestedSave}
        {...modeProps}
      />
    )
  },
  marketLocation: {
    displayName: ObjectName.MarketLocation,
    Component: ({
      isOpen,
      modeProps,
      siteId,
      stackId,
      onClose,
      onNestedSave
    }: WizardComponentProps<MeteringOrMarketLocation>) => (
      <MarketLocationWizard
        context="nested"
        isOpen={isOpen}
        siteId={siteId}
        size="lg"
        stackId={stackId}
        onClose={onClose}
        onNestedSave={onNestedSave}
        {...modeProps}
      />
    )
  }
} satisfies { [K in WizardKey]: FieldWizardEntry<WizardPayloadMap[K]> };

export { FIELD_WIZARD_REGISTRY };
export type { WizardKey, WizardPayloadMap };
